import { useState, useEffect, useRef, useCallback } from 'react';
import { Location } from '@/types';
import LocationService from '@/services/LocationService';

interface UseLocationObserverOptions {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
  fallbackLocations?: Location[];
}

interface UseLocationObserverReturn {
  locations: Location[];
  isLoading: boolean;
  error: string | null;
  targetRef: React.RefObject<HTMLElement | null>;
  isVisible: boolean;
  refetch: () => Promise<void>;
}

// Cache is now handled by LocationService

/**
 * Custom hook for lazy loading locations using Intersection Observer
 * Only fetches locations when the target element becomes visible
 */
export const useLocationObserver = ({
  threshold = 0.1,
  rootMargin = '50px',
  enabled = true,
  fallbackLocations = [],
}: UseLocationObserverOptions = {}): UseLocationObserverReturn => {
  const [locations, setLocations] = useState<Location[]>(fallbackLocations);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  
  const targetRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fetchLocations = useCallback(async (): Promise<void> => {
    if (!enabled || hasFetched) return;

    // Check cache first
    const cachedData = LocationService.getCachedLocations();
    if (cachedData) {
      setLocations(cachedData);
      setHasFetched(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use new observer-based fetch method with smaller pagination
      const fetchedLocations = await LocationService.fetchLocationsOnDemand({ 
        perPage: 20, 
        useCache: true 
      });
      setLocations(fetchedLocations);
      setHasFetched(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch locations';
      setError(errorMessage);
      console.error('Location fetch error:', err);
      
      // Use fallback locations if available
      if (fallbackLocations.length > 0) {
        setLocations(fallbackLocations);
      }
    } finally {
      setIsLoading(false);
    }
  }, [enabled, hasFetched, fallbackLocations]);

  const refetch = useCallback(async (): Promise<void> => {
    setHasFetched(false);
    // Clear cache to force fresh fetch
    LocationService.clearCache();
    await fetchLocations();
  }, [fetchLocations]);

  // Setup Intersection Observer
  useEffect(() => {
    if (!enabled || !targetRef.current) return;

    const target = targetRef.current;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);
        
        if (visible && !hasFetched) {
          fetchLocations();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observerRef.current.observe(target);

    return () => {
      if (observerRef.current && target) {
        observerRef.current.unobserve(target);
      }
    };
  }, [enabled, threshold, rootMargin, fetchLocations, hasFetched]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // If locations are provided as fallback and no fetch has occurred, use them
  useEffect(() => {
    if (fallbackLocations.length > 0 && locations.length === 0 && !hasFetched) {
      setLocations(fallbackLocations);
    }
  }, [fallbackLocations, locations.length, hasFetched]);

  return {
    locations,
    isLoading,
    error,
    targetRef,
    isVisible,
    refetch,
  };
};

export default useLocationObserver;